/**
 * Interprets a search query as a calendar date, if it looks like one.
 * Supported shapes: ISO "2026-07-15", European "15.07.2026" / "15.07." / "15.07"
 * (current year when omitted), and US "7/15/2026" / "7/15" (current year when
 * omitted). Returns null when the text isn't a valid date.
 */
export function parseDateQuery(query: string, today: Date = new Date()): Date | null {
  const text = query.trim();

  const iso = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (iso) {
    return buildDate(Number(iso[1]), Number(iso[2]), Number(iso[3]));
  }

  const european = text.match(/^(\d{1,2})\.(\d{1,2})\.?(\d{4})?$/);
  if (european) {
    const year = european[3] ? Number(european[3]) : today.getFullYear();
    return buildDate(year, Number(european[2]), Number(european[1]));
  }

  const us = text.match(/^(\d{1,2})\/(\d{1,2})(?:\/(\d{4}))?$/);
  if (us) {
    const year = us[3] ? Number(us[3]) : today.getFullYear();
    return buildDate(year, Number(us[1]), Number(us[2]));
  }

  return null;
}

/** Formats a date as the YYYY-MM-DD string the calendar route expects. */
export function toDateParam(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

function buildDate(year: number, month: number, day: number): Date | null {
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const date = new Date(year, month - 1, day);
  // Reject overflowed dates like 31.02 that Date would roll into March.
  const isReal =
    date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
  return isReal ? date : null;
}
